import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { classifyGridMiniCell, type GridMiniCellState } from "./gridMiniCell.js";
import { drawShapeLines } from "./shapeRenderer.js";
import type { Direction, ModuleGridLane } from "../models/ModuleGrid.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import { resolveSpaceshipLanePage } from "./spaceshipLanePager.js";

export interface GridUnlockConfirmPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly gridWidth: number;
  readonly gridHeight: number;
  /** 해제 대상 셀 좌표(미니맵에서 강조). */
  readonly targetX: number;
  readonly targetY: number;
  /** Start/Fire 엔드포인트 좌표. 팝업 미니맵은 Start만 표시하고 Fire는 데이터 호환 필드로만 받는다. */
  readonly start: { readonly x: number; readonly y: number };
  readonly fire: { readonly x: number; readonly y: number };
  /** 우주선별 다중 배선. 미지정 시 start/fire 단일 배선으로 렌더한다. */
  readonly lanes?: readonly ModuleGridLane[];
  /** 전투탭에서 보고 있던 배선 페이지. 다중 배선 우주선의 팝업 미니 그리드 window에 사용한다. */
  readonly visibleLaneIndex?: number;
  /** 다중 배선 팝업에서 한 번에 표시할 최대 행 수. 미지정 시 전체 높이를 유지한다. */
  readonly maxVisibleRows?: number;
  /** 표시용 비용 문자열 (formatBig 적용 — 미니맵 표기와 통일). BigNumber 정밀값은 호출자(battleBoot)가 차감·잔액판정에 사용. */
  readonly costText: string;
  /** 비용 통화 아이콘 텍스처 키 (no-hardcode — 호출자가 데이터 키를 전달). */
  readonly costIconKey: string;
  readonly isUnlocked: (x: number, y: number) => boolean;
  /** 셀의 설치 모듈 포트 방향(없으면 null) — 미니맵에 모듈 모양(포트선)을 그린다. null=빈칸(occupied 판정도 겸함). */
  readonly cellPorts: (x: number, y: number) => readonly Direction[] | null;
  /** Yes 클릭 시점 잔액 충분 여부 — 실패 시 흔들림 피드백(팝업 유지, 차감 없음). */
  readonly canAfford: () => boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const PANEL_W = 280;
const PANEL_H = 230;
/** 미니맵 셀 한 변(px) + 셀 간격(px). */
const MINI_CELL = 22;
const MINI_GAP = 3;

/**
 * 미니맵 셀 상태별 채움/테두리 색 + 테두리 굵기 — PALETTE_NUM 토큰만(hex 리터럴 금지, themeStatic 게이트).
 * target은 채움을 잠금색으로 두고 밝은 시안 테두리를 굵게 그려 "해금 예정"을 테두리 하이라이트로 강조.
 * occupied(설치됨)는 모듈 배치색 채움 + 셀 포트선(buildMiniGrid의 drawShapeLines)으로 빈 해금칸(unlocked)과 구분.
 */
const MINI_CELL_FILL: Record<GridMiniCellState, number> = {
  target: PALETTE_NUM.gridCellEmpty,
  occupied: PALETTE_NUM.gridCellFilled,
  unlocked: PALETTE_NUM.gridCellOnPath,
  locked: PALETTE_NUM.gridCellEmpty,
};
const MINI_CELL_STROKE: Record<GridMiniCellState, number> = {
  target: PALETTE_NUM.gridPathGlow,
  occupied: PALETTE_NUM.gridCellStroke,
  unlocked: PALETTE_NUM.gridCellStroke,
  locked: PALETTE_NUM.gridCellEmptyStroke,
};
const MINI_CELL_STROKE_W: Record<GridMiniCellState, number> = {
  target: 3,
  occupied: 1,
  unlocked: 1,
  locked: 1,
};
/** Start 엔드포인트 셀 — 실제 그리드(renderEndpoint)와 동일 색 토큰 + 아이콘 크기. */
const MINI_ENDPOINT_FILL = PALETTE_NUM.gridCellFilled;
const MINI_ENDPOINT_STROKE = PALETTE_NUM.gridCellStroke;
const MINI_ENDPOINT_ICON = 14;
/** 설치 모듈 포트선 반경(셀 중심→경계) — 셀이 작아 여백 3px. */
const MINI_PORT_HALF = MINI_CELL / 2 - 3;

/**
 * 그리드 잠금 칸 해제 확인 팝업 — ModuleInfoPopup 패턴(ModalLayer + NineSlicePanel).
 * 미니 그리드로 해제 대상 셀을 그래픽 강조 + Yes(비용 아이콘+비용량) / No(localize) 버튼.
 * 골드 부족 시 Yes 클릭 → canAfford 게이트가 막고 흔들림 피드백(팝업 유지), 충분 시에만 onConfirm.
 * 실제 차감/해금은 셸(GameScene → GridCommands.unlockCell)이 onConfirm에서 수행 — 본 팝업은 확인 UI만.
 */
export class GridUnlockConfirmPopup extends ModalLayer {
  private readonly panel: NineSlicePanel;
  /** 패널 기준 x(절대) — 흔들림 tween 시작·복원 기준(흔들린 중간값 누적 방지). */
  private readonly panelBaseX: number;
  private shakeTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, opts: GridUnlockConfirmPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onCancel(),
    });

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const top = cy - PANEL_H / 2;
    this.panelBaseX = cx;
    // dim 클릭 닫기 — 패널 영역 제외(패널 내부 클릭은 안 닫힘).
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, PANEL_H);

    this.panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: PANEL_H,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(this.panel);

    const title = scene.add
      .text(cx, top + 22, t("grid.unlockConfirm.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, getTextDirection()), top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onCancel());
    this.contentContainer.add([title, closeBtn]);

    // 미니 그리드 — 해제 대상 셀을 그래픽으로 강조(어느 행/열을 여는지 직관 표시).
    this.buildMiniGrid(scene, opts, top + 48);

    // Yes 버튼 — 비용 아이콘 + 비용량. 부족 시 흔들림(팝업 유지).
    const yesBtn = new NineSliceButton(scene, cx - 62, top + PANEL_H - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: opts.costText,
      iconKey: opts.costIconKey,
      widthOverride: 108,
    });
    yesBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      if (opts.canAfford()) opts.onConfirm();
      else this.denyFeedback();
    });
    // No 버튼 — localize.
    const noBtn = new NineSliceButton(scene, cx + 62, top + PANEL_H - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("grid.unlockConfirm.no"),
      widthOverride: 108,
    });
    noBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onCancel());
    this.contentContainer.add([yesBtn, noBtn]);
  }

  /**
   * 미니 그리드 — 실제 그리드 축소판. 열 범위를 Start(좌)와 그리드 본체로 잡고,
   * 설치된 칸은 그 셀의 포트선(모듈 모양)을 그린다. y0=그리드 상단 픽셀.
   */
  private buildMiniGrid(scene: Phaser.Scene, opts: GridUnlockConfirmPopupOptions, y0: number): void {
    const step = MINI_CELL + MINI_GAP;
    const lanes = opts.lanes && opts.lanes.length > 0 ? opts.lanes : [{ start: opts.start, fire: opts.fire }];
    const page = resolveSpaceshipLanePage(
      opts.gridHeight,
      opts.lanes,
      opts.visibleLaneIndex ?? 0,
      opts.maxVisibleRows ?? opts.gridHeight,
    );
    // 그리드(0..gw-1) + Start(범위 밖 x)만 포함한다. Fire는 게임뷰와 동일하게 표시하지 않는다.
    const endpointXs = lanes.map((lane) => lane.start.x);
    const minX = Math.min(...endpointXs, 0);
    const maxX = Math.max(...endpointXs, opts.gridWidth - 1);
    const totalCols = maxX - minX + 1;
    const totalW = totalCols * MINI_CELL + (totalCols - 1) * MINI_GAP;
    const startX = this.panelBaseX - totalW / 2 + MINI_CELL / 2;
    const colPx = (gx: number) => startX + (gx - minX) * step;
    const rowPy = (gy: number) => y0 + MINI_CELL / 2 + (gy - page.rowOffset) * step;

    for (let gy = page.rowOffset; gy < page.rowOffset + page.visibleRows; gy++) {
      for (let gx = minX; gx <= maxX; gx++) {
        const px = colPx(gx);
        const py = rowPy(gy);
        const startEndpoint = lanes.some((lane) => gx === lane.start.x && gy === lane.start.y);
        if (startEndpoint) {
          this.renderMiniEndpoint(scene, px, py, "icon_grid_start", PALETTE_NUM.endpointStart);
          continue;
        }
        // Start 열의 다른 행(범위 밖)은 빈 공간으로 둔다.
        if (gx < 0 || gx >= opts.gridWidth) continue;

        const state = classifyGridMiniCell(
          gx,
          gy,
          opts.targetX,
          opts.targetY,
          opts.isUnlocked,
          (cx, cy) => opts.cellPorts(cx, cy) !== null,
        );
        const rect = scene.add
          .rectangle(px, py, MINI_CELL, MINI_CELL, MINI_CELL_FILL[state])
          .setStrokeStyle(MINI_CELL_STROKE_W[state], MINI_CELL_STROKE[state]);
        this.contentContainer.add(rect);
        // 설치된 칸은 그 셀의 포트선(모듈 모양)을 그려 빈 해금칸과 구분.
        if (state === "occupied") {
          const ports = opts.cellPorts(gx, gy);
          if (ports && ports.length > 0) {
            const g = scene.add.graphics().setPosition(px, py);
            drawShapeLines(g, ports, MINI_PORT_HALF, PALETTE_NUM.portOn, 1);
            this.contentContainer.add(g);
          }
        }
      }
    }
  }

  /** Start 엔드포인트 미니 셀 — 배경 + 흰 실루엣 아이콘(팔레트 tint). 실제 그리드 renderEndpoint 축소판. */
  private renderMiniEndpoint(
    scene: Phaser.Scene,
    px: number,
    py: number,
    iconKey: string,
    tint: number,
  ): void {
    const rect = scene.add
      .rectangle(px, py, MINI_CELL, MINI_CELL, MINI_ENDPOINT_FILL)
      .setStrokeStyle(1, MINI_ENDPOINT_STROKE);
    const icon = scene.add
      .image(px, py, iconKey)
      .setDisplaySize(MINI_ENDPOINT_ICON, MINI_ENDPOINT_ICON)
      .setTint(tint);
    this.contentContainer.add([rect, icon]);
  }

  /**
   * 닫힘 시 패널에 건 흔들림 tween을 먼저 정리 — ModalLayer.close()의 killTweensOf(this)는
   * 자식(panel)에 건 tween을 죽이지 못해(W1), 흔들림 중 닫으면 destroy 후 onComplete가 소멸된 패널을 건드릴 수 있다.
   */
  override close(): void {
    this.shakeTween?.stop();
    this.shakeTween = undefined;
    super.close();
  }

  /** 골드 부족 시 — 패널 좌우 흔들림(±6px, ~60ms ×3). canAfford 게이트가 호출. */
  private denyFeedback(): void {
    this.shakeTween?.stop();
    this.panel.setX(this.panelBaseX);
    this.shakeTween = this.scene.tweens.add({
      targets: this.panel,
      x: { from: this.panelBaseX - 6, to: this.panelBaseX + 6 },
      duration: 60,
      yoyo: true,
      repeat: 2,
      ease: "Sine.inOut",
      onComplete: () => this.panel.setX(this.panelBaseX),
    });
  }
}
